import Vue from 'vue';
import './components';
import Vuelidate from 'vuelidate';
import VueTheMask from 'vue-the-mask';
import axios from 'axios';
import * as Sentry from '@sentry/vue';
import store from './store';
import './scss/main.scss';
import './formie/formie';
import Announcement from './components/widgets/Announcement';
import CookiebotVideoFallback from './helpers/cookiebotVideoFallback';
import { captureEntryPath, captureMarketingParams } from './googleAnalytics/sgtm';
import { trackCtaClick } from './googleAnalytics/funnel';

Vue.config.productionTip = false;
// Vue.config.devtools = true;

const isProduction = process.env.NODE_ENV === 'production';
const sentryDsn = isProduction ? 'https://687567c6261b4f498adbbea99787d8d4@sentry.yournext.agency/58' : null;

// Our code ships only from the Vite bundle. Browser-injected scripts (Chrome
// iOS's Google Translate, extensions) are attributed by WebKit to the page URL
// itself, so a URL denylist can't catch them - instead drop any exception whose
// stack never touches the bundle. Events without frames are kept.
const isFromOurBundle = (frame) => (frame.filename || '').includes('/dist/assets/');

const isThirdPartyError = (event) => {
  const frames = (event.exception?.values || [])
    .flatMap((exception) => exception.stacktrace?.frames || []);

  return frames.length > 0 && !frames.some(isFromOurBundle);
};

Sentry.init({
  Vue,
  dsn: sentryDsn,
  replaysSessionSampleRate: 0.4,
  replaysOnErrorSampleRate: 1.0,
  tracePropagationTargets: ['https://ziz.nl'],
  denyUrls: [/^(chrome|moz|safari|safari-web)-extension:\/\//i, /^webkit-masked-url:\/\//i],
  beforeSend: (event) => (isThirdPartyError(event) ? null : event),
  integrations: [
    Sentry.replayIntegration(),
    Sentry.replayCanvasIntegration(),
  ],
});

// Init announcement
Announcement.init();

Vue.use(Vuelidate);
Vue.use(VueTheMask);

const CSRF_TOKEN_URL = '/actions/blitz/csrf/token';

let csrfTokenRequest = null;

// `credentials` is explicit because older WebKit omits cookies on fetch by
// default, which returns a token that can never validate against the cookie.
const requestCsrfToken = async () => {
  const response = await fetch(CSRF_TOKEN_URL, { credentials: 'same-origin' });

  if (!response.ok) {
    throw new Error(`CSRF token request failed with status ${response.status}`);
  }

  const token = (await response.text()).trim();

  if (!token) {
    throw new Error('CSRF token request returned an empty body');
  }

  return token;
};

// Craft mints a fresh CRAFT_CSRF_TOKEN cookie for every caller that arrives
// without one, and only the last Set-Cookie survives. Blitz serves the form
// pages from its cache, so no cookie ships with the HTML and every visitor
// starts cold: two concurrent token fetches leave one of them holding a token
// that no longer matches the cookie, which Craft rejects with a 400. Sharing a
// single in-flight request keeps every caller on the same token.
const getCsrfToken = () => {
  if (!csrfTokenRequest) {
    csrfTokenRequest = requestCsrfToken()
      .catch((error) => {
        // Reported rather than swallowed - this used to send
        // `X-CSRF-Token: undefined` silently.
        Sentry.captureException(error);

        return null;
      })
      // Cleared once settled, so this only deduplicates callers that overlap.
      // Holding it would turn the shared request into a page-lifetime token
      // cache, and /consult stays open for as long as the form takes to fill,
      // so a cookie that is rotated or evicted meanwhile must be refetched.
      .finally(() => {
        csrfTokenRequest = null;
      });
  }

  return csrfTokenRequest;
};

Vue.prototype.$http = axios.create({
  timeout: 120000,
});

Vue.prototype.$http.interceptors.request.use(async (config) => {
  const selfConfig = { ...config };
  const token = await getCsrfToken();

  if (token) {
    selfConfig.headers = { ...selfConfig.headers, 'X-CSRF-Token': token };
  }

  return selfConfig;
});

store.commit('SET_SITE_LOCALE', document.documentElement.lang);

captureMarketingParams();
captureEntryPath();

// Key entry CTAs carry data-cta-id in the templates (ZIZ-1308). Captured before the
// navigation they start; the id is a fixed, condition-neutral name.
document.addEventListener('click', (event) => {
  const cta = event.target.closest?.('[data-cta-id]');
  if (!cta) return;

  let destination = null;
  try {
    destination = cta.href ? new URL(cta.href, window.location.href).pathname : null;
  } catch {
    // Not a navigable link: report the click without a destination.
  }
  trackCtaClick(cta.dataset.ctaId, destination);
}, true);

// eslint-disable-next-line no-new
new Vue({
  store,
  el: '#app',
  delimiters: ['${', '}'],
});

CookiebotVideoFallback.init();
